import { useEffect, useState } from 'react';
import { BankOutlined, CopyOutlined, PercentageOutlined, SafetyCertificateOutlined, WalletOutlined } from '@ant-design/icons';
import { Alert, App, Button, Form, Input, InputNumber, Tag } from 'antd';
import { API_BASE, api } from '../../api';
import { ErrorNotice, FormSkeleton, PageHeader, Panel, StatCard } from '../../components';
import { timeAgo, useLoad } from '../../lib';
import { BackToSettings } from './BackToSettings';
/**
 * Where Razorpay sends payment events: the API's public address. Uses REACT_APP_API_PUBLIC_URL, then the same
 * REACT_APP_KIVONEX_API the console calls, then this site's origin (development proxy).
 */
export function webhookUrl() {
    const base = process.env.REACT_APP_API_PUBLIC_URL?.replace(/\/+$/, '') || API_BASE || window.location.origin;
    return `${base}/v1/payments/razorpay/webhook`;
}
export function PaymentSettings() {
    const { message } = App.useApp();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState(null);
    const [dirty, setDirty] = useState(false);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/settings/payments').then((body) => body.settings));
    const settings = data;
    const keyId = Form.useWatch('keyId', form);
    // A new key id needs its own secret; the saved one belongs to the old key.
    const secretNeeded = Boolean(keyId?.trim()) && (!settings?.keySecretHint || keyId?.trim() !== settings.keyId);
    useEffect(() => {
        if (!settings)
            return;
        form.setFieldsValue({ keyId: settings.keyId, keySecret: '', webhookSecret: '', gstRate: settings.gstRate, seller: settings.seller, accountUrl: settings.accountUrl });
        setDirty(false);
    }, [settings, form]);
    /** The form as the API takes it; a blank secret keeps the saved one. */
    function body(values) {
        return {
            keyId: values.keyId?.trim() ?? '',
            keySecret: values.keySecret?.trim() ?? '',
            webhookSecret: values.webhookSecret?.trim() ?? '',
            gstRate: values.gstRate,
            seller: { name: values.seller?.name ?? '', gstin: values.seller?.gstin ?? '', address: values.seller?.address ?? '', state: values.seller?.state ?? '' },
            accountUrl: values.accountUrl?.trim() ?? ''
        };
    }
    async function save(values) {
        setSaving(true);
        try {
            const saved = await api('/v1/admin/settings/payments', { method: 'PUT', body: JSON.stringify(body(values)) });
            message.success(saved.settings.configured ? 'Saved. Payments are on.' : 'Saved. Payments are off until a key is added.');
            setTest(null);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not save the settings');
        }
        finally {
            setSaving(false);
        }
    }
    async function runTest() {
        try {
            await form.validateFields();
        }
        catch {
            return;
        }
        setTesting(true);
        setTest(null);
        try {
            await api('/v1/admin/settings/payments/test', { method: 'POST', body: JSON.stringify(body(form.getFieldsValue())) });
            setTest({ ok: true, text: 'Razorpay accepted the key id and secret.' });
        }
        catch (err) {
            setTest({ ok: false, text: err instanceof Error ? err.message : 'The keys could not be checked' });
        }
        finally {
            setTesting(false);
        }
    }
    async function copyWebhook() {
        try {
            await navigator.clipboard.writeText(webhookUrl());
            message.success('Webhook URL copied');
        }
        catch {
            message.error('Could not copy the URL');
        }
    }
    const on = Boolean(settings?.configured);
    const live = settings?.keyId.startsWith('rzp_live_');
    return (<>
      <PageHeader title="Payments" description="Customers buy plans in the account panel and pay through Razorpay. The keys stay on this server; customers never see the secret." actions={<BackToSettings />}/>
      <ErrorNotice error={error}/>
      {settings?.secretUnreadable && (<Alert type="warning" showIcon title="A saved Razorpay secret cannot be read on this server" description="The server's settings key was replaced or lost. Enter the key secret and webhook secret again." style={{ marginBottom: 20 }}/>)}

      <div className="stats">
        <StatCard label="Payments" tone={on ? 'green' : 'slate'} icon={<WalletOutlined />} loading={loading && !data} value={on ? 'On' : 'Off'} foot={settings?.updatedAt ? `Changed ${timeAgo(settings.updatedAt)}` : 'Not set up yet'}/>
        <StatCard label="Razorpay mode" tone={live ? 'indigo' : 'amber'} icon={<SafetyCertificateOutlined />} loading={loading && !data} value={settings?.keyId ? (live ? 'Live' : 'Test') : '—'} foot={live ? 'Real money is charged' : settings?.keyId ? 'No real money moves' : 'No key yet'}/>
        <StatCard label="GST rate" tone="sky" icon={<PercentageOutlined />} loading={loading && !data} value={`${settings?.gstRate ?? 0}%`} foot="Included in plan prices"/>
      </div>

      {loading && !data ? (<Panel flush title="Razorpay" description="Checkout, payment confirmation and invoices.">
          <FormSkeleton sections={4}/>
        </Panel>) : (<Form form={form} layout="vertical" requiredMark={false} onFinish={(values) => void save(values)} onValuesChange={() => setDirty(true)}>
          <Panel flush title="Razorpay" description="Checkout, payment confirmation and invoices." extra={on ? (<Tag color="success" variant="filled">
                  <span className="status-dot"/>
                  Payments are on
                </Tag>) : (<Tag variant="filled">Payments are off</Tag>)}>
            <div className="form-section">
              <div>
                <h4>API keys</h4>
                <p>
                  In the Razorpay Dashboard, Account &amp; Settings › API Keys. Use test keys (rzp_test_…) until you are ready, then live keys (rzp_live_…). The
                  secret is stored encrypted and never shown again.
                </p>
              </div>
              <div>
                <Form.Item label="Key id" name="keyId" rules={[{ pattern: /^rzp_(test|live)_[A-Za-z0-9]+$/, message: 'A Razorpay key id starts with rzp_test_ or rzp_live_.' }]} extra="Without a key id and secret, customers cannot buy plans.">
                  <Input autoComplete="off" className="mono" placeholder="rzp_test_…"/>
                </Form.Item>
                <Form.Item label="Key secret" name="keySecret" rules={[{ required: secretNeeded, message: 'Enter the key secret for this key id.' }]} extra={settings?.keySecretHint && !secretNeeded ? (<span>
                        <SafetyCertificateOutlined style={{ color: 'var(--brand)' }}/> A secret ending <span className="mono">{settings.keySecretHint}</span> is saved. Leave
                        blank to keep it.
                      </span>) : ('Razorpay shows it once, when the key is made.')}>
                  <Input.Password autoComplete="new-password" className="mono" placeholder={settings?.keySecretHint && !secretNeeded ? '••••••••••••' : ''}/>
                </Form.Item>
              </div>
            </div>

            <div className="form-section">
              <div>
                <h4>Webhook</h4>
                <p>
                  So a payment counts even when the customer closes the browser before it returns. In the Razorpay Dashboard, Account &amp; Settings › Webhooks, add
                  this URL with the events <span className="mono">payment.captured</span>, <span className="mono">order.paid</span> and{' '}
                  <span className="mono">payment.failed</span>, and a secret of your choice.
                </p>
              </div>
              <div>
                <Form.Item label="Webhook URL" extra="The API's public address. Razorpay must be able to reach it over HTTPS.">
                  <Input readOnly value={webhookUrl()} className="mono" aria-label="Webhook URL" suffix={<Button type="text" size="small" icon={<CopyOutlined />} aria-label="Copy webhook URL" onClick={() => void copyWebhook()}/>}/>
                </Form.Item>
                <Form.Item label="Webhook secret" name="webhookSecret" extra={settings?.webhookSecretHint ? (<span>
                        <SafetyCertificateOutlined style={{ color: 'var(--brand)' }}/> A secret ending <span className="mono">{settings.webhookSecretHint}</span> is saved. Leave
                        blank to keep it.
                      </span>) : ('The same secret you enter for the webhook in Razorpay.')}>
                  <Input.Password autoComplete="new-password" className="mono" placeholder={settings?.webhookSecretHint ? '••••••••••••' : ''}/>
                </Form.Item>
              </div>
            </div>

            <div className="form-section">
              <div>
                <h4>Invoices</h4>
                <p>
                  Printed on every invoice. Plan prices include GST; an invoice shows CGST and SGST when the customer is in your state (or gives none), otherwise
                  IGST.
                </p>
              </div>
              <div>
                <div className="form-row">
                  <Form.Item label="Seller name" name={['seller', 'name']} rules={[{ required: true, whitespace: true, message: 'Enter the name printed on invoices.' }]}>
                    <Input placeholder="Kivonex Technologies" prefix={<BankOutlined style={{ color: 'var(--muted)' }}/>}/>
                  </Form.Item>
                  <Form.Item label="GST rate" name="gstRate" rules={[{ required: true }]}>
                    <InputNumber placeholder="18" min={0} max={28} precision={2} suffix="%" style={{ width: '100%' }}/>
                  </Form.Item>
                </div>
                <div className="form-row">
                  <Form.Item label="GSTIN" name={['seller', 'gstin']} normalize={(value) => value.toUpperCase()} rules={[{ pattern: /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, message: 'Enter a valid 15-character GSTIN.' }]}>
                    <Input placeholder="19ABCDE1234F1Z5" className="mono" maxLength={15} autoComplete="off"/>
                  </Form.Item>
                  <Form.Item label="State" name={['seller', 'state']} extra="Decides CGST and SGST or IGST.">
                    <Input placeholder="West Bengal"/>
                  </Form.Item>
                </div>
                <Form.Item label="Address" name={['seller', 'address']}>
                  <Input.TextArea placeholder="Registered address as on the GST certificate" rows={2}/>
                </Form.Item>
              </div>
            </div>

            <div className="form-section">
              <div>
                <h4>Account panel</h4>
                <p>Where customers sign in with their mobile number and buy plans. The desktop app shows this address when a customer has no plan.</p>
              </div>
              <div>
                <Form.Item label="Account panel URL" name="accountUrl" rules={[{ required: true, type: 'url', message: 'Enter the full address, e.g. https://app.kivonex.in.' }]}>
                  <Input className="mono" placeholder="https://account.example.com" autoComplete="off"/>
                </Form.Item>
              </div>
            </div>

            {test && (<div style={{ padding: '0 20px 20px' }}>
                <Alert type={test.ok ? 'success' : 'error'} showIcon title={test.text}/>
              </div>)}
            <div className="form-actions">
              <Button onClick={() => void runTest()} loading={testing}>
                Test connection
              </Button>
              <Button type="primary" htmlType="submit" loading={saving} disabled={!dirty && Boolean(settings?.updatedAt)}>
                Save changes
              </Button>
            </div>
          </Panel>
        </Form>)}
    </>);
}
